import React, { useEffect, useState, Suspense, lazy } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Check, ArrowLeft, ShieldCheck, Truck, Sparkles, Zap, X, Rotate3d } from "lucide-react";
import { api } from "@/lib/api";
import { eur } from "@/lib/format";
import { useCart } from "@/context/CartContext";
import Seo from "@/components/Seo";
import { KineticHeading } from "@/components/motion/KineticHeading";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import ExpressCheckout from "@/components/site/ExpressCheckout";

const Model3DViewer = lazy(() => import("@/components/site/Model3DViewer"));

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addItem, items, setBuyNow, clearBuyNow, setOpen } = useCart();
  const [product, setProduct] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [view3D, setView3D] = useState(false);
  const [variant, setVariant] = useState(null);
  const [added, setAdded] = useState(false);
  const [showBuyModal, setShowBuyModal] = useState(false);

  useEffect(() => {
    setProduct(null);
    api.get(`/products/${slug}`).then((r) => {
      setProduct(r.data);
      setActiveImg(0);
      setView3D(false);
      setVariant(r.data.variants?.length ? 0 : null);
    }).catch(() => {});
  }, [slug]);

  if (!product) {
    return <div className="pt-40 pb-40 text-center ee-overline animate-pulse">Loading…</div>;
  }

  const price = variant != null && product.variants?.[variant] ? product.variants[variant].price : product.price;

  const onAdd = () => {
    addItem(product, variant, 1);
    setAdded(true);
    setTimeout(() => setAdded(false), 1800);
  };

  const goCheckoutSingle = () => {
    setBuyNow(product, variant, 1);
    setOpen(false);
    navigate("/cart");
  };

  const onBuyNow = () => {
    if (items.length === 0) {
      goCheckoutSingle();
      return;
    }
    setShowBuyModal(true);
  };

  const buyOnlyThis = () => {
    setShowBuyModal(false);
    goCheckoutSingle();
  };

  const mergeAndCheckout = () => {
    setShowBuyModal(false);
    clearBuyNow();
    addItem(product, variant, 1);
    setOpen(false);
    navigate("/cart");
  };

  return (
    <div className="pt-32 pb-28 px-6 md:px-12 lg:px-24 max-w-[1600px] mx-auto">
      <Seo
        title={product.name}
        description={product.meta_description || product.short_description}
        image={product.images?.[0]}
        type="product"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Product",
          name: product.name,
          image: (product.images || []).map((i) => (i.startsWith("http") ? i : `${window.location.origin}${i}`)),
          description: product.description,
          sku: product.slug,
          category: product.category_name,
          material: product.materials || undefined,
          brand: { "@type": "Brand", name: "Equine Evolve" },
          url: `${window.location.origin}/product/${product.slug}`,
          offers: {
            "@type": "Offer",
            priceCurrency: "EUR",
            price: product.price,
            availability: product.in_stock ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
            itemCondition: "https://schema.org/NewCondition",
            url: `${window.location.origin}/product/${product.slug}`,
            seller: { "@type": "Organization", name: "Equine Evolve" },
            hasMerchantReturnPolicy:
              product.category === "jewellery"
                ? { "@type": "MerchantReturnPolicy", applicableCountry: "IE", returnPolicyCategory: "https://schema.org/MerchantReturnNotPermitted" }
                : { "@type": "MerchantReturnPolicy", applicableCountry: "IE", returnPolicyCategory: "https://schema.org/MerchantReturnFiniteReturnWindow", merchantReturnDays: 14, returnMethod: "https://schema.org/ReturnByMail" },
          },
        }}
      />

      <Link to="/shop" className="inline-flex items-center gap-2 text-[#A8A29E] hover:text-[#D4AF37] text-sm mb-10">
        <ArrowLeft size={16} /> Back to shop
      </Link>

      <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
        {/* Gallery */}
        <div className="lg:sticky lg:top-28">
          <motion.div
            key={view3D ? "3d" : activeImg}
            initial={{ opacity: 0, scale: 1.02 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            className="relative aspect-[4/5] overflow-hidden bg-[#1C1917] border ee-hairline"
            style={view3D ? { touchAction: "none" } : undefined}
          >
            {view3D && product.model_3d ? (
              <>
                <Suspense fallback={<div className="absolute inset-0 grid place-items-center ee-overline animate-pulse">Loading 3D…</div>}>
                  <Model3DViewer url={product.model_3d} />
                </Suspense>
                <span className="pointer-events-none absolute bottom-3 left-1/2 -translate-x-1/2 bg-[#0C0A09]/80 border border-[#D4AF37]/30 px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-[#A8A29E]">
                  Drag to rotate
                </span>
              </>
            ) : (
              <img src={product.images?.[activeImg]} alt={product.name} className="h-full w-full object-cover" data-testid="product-main-image" />
            )}
          </motion.div>
          {(product.images?.length > 1 || product.model_3d) && (
            <div className="flex gap-4 mt-4 flex-wrap">
              {product.images?.map((img, i) => (
                <button
                  key={i}
                  onClick={() => { setActiveImg(i); setView3D(false); }}
                  data-testid={`product-thumb-${i}`}
                  className={`h-24 w-20 overflow-hidden border transition-colors ${i === activeImg && !view3D ? "border-[#D4AF37]" : "ee-hairline"}`}
                >
                  <img src={img} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
              {product.model_3d && (
                <button
                  onClick={() => setView3D(true)}
                  data-testid="product-3d-thumb"
                  className={`h-24 w-20 flex flex-col items-center justify-center gap-1.5 border transition-colors ${view3D ? "border-[#D4AF37] text-[#D4AF37]" : "ee-hairline text-[#A8A29E] hover:text-[#D4AF37]"}`}
                >
                  <Rotate3d size={20} strokeWidth={1.4} />
                  <span className="text-[9px] uppercase tracking-[0.15em]">360° · 3D</span>
                </button>
              )}
            </div>
          )}
        </div>

        {/* Info */}
        <div>
          <p className="ee-overline mb-4">{product.category_name}</p>
          <div data-testid="product-title">
            <KineticHeading
              as="h1"
              text={product.name}
              className="font-display text-4xl md:text-6xl tracking-tight leading-none flex flex-wrap"
              stagger={0.06}
            />
          </div>
          {product.inspired_by && <p className="text-[#A39B95] italic mt-4 text-lg">{product.inspired_by}</p>}

          <p className="font-display text-4xl ee-gold-text mt-8" data-testid="product-price">
            {eur(price)}
            <span className="text-[#57534E] text-sm ml-3 font-sans">excl. VAT</span>
          </p>
          {product.category === "jewellery" && (
            <p className="text-xs text-[#A8A29E] mt-2 flex items-center gap-2" data-testid="gold-rate-note">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#D4AF37] animate-pulse" />
              Price reflects today's gold rate · final price confirmed at measurement
            </p>
          )}
          {product.price_note && (
            <p className="text-xs text-[#A8A29E] mt-1.5" data-testid="price-note">{product.price_note}</p>
          )}

          <p className="text-[#A8A29E] leading-relaxed mt-8">{product.description}</p>

          {product.variants?.length > 0 && (
            <div className="mt-8">
              <p className="ee-overline mb-3">Options</p>
              <div className="flex flex-wrap gap-3">
                {product.variants.map((v, i) => (
                  <button
                    key={i}
                    onClick={() => setVariant(i)}
                    data-testid={`variant-${i}`}
                    className={`px-5 py-3 text-sm border transition-colors ${variant === i ? "bg-[#D4AF37] text-[#0C0A09] border-[#D4AF37]" : "ee-hairline text-[#A8A29E] hover:text-[#D4AF37]"}`}
                  >
                    {v.name} · {eur(v.price)}
                  </button>
                ))}
              </div>
            </div>
          )}

          {product.details?.length > 0 && (
            <ul className="mt-8 space-y-3">
              {product.details.map((d) => (
                <li key={d} className="flex items-center gap-3 text-[#A8A29E] text-sm">
                  <Check size={16} className="ee-gold-text" /> {d}
                </li>
              ))}
            </ul>
          )}

          {(product.materials || product.care || product.lead_time) && (
            <div className="mt-8 border-t ee-hairline pt-6 space-y-3 text-sm" data-testid="product-specs">
              {product.materials && <div className="flex flex-col sm:flex-row gap-1 sm:gap-3"><span className="ee-overline sm:w-28 flex-shrink-0">Materials</span><span className="text-[#A8A29E]">{product.materials}</span></div>}
              {product.lead_time && <div className="flex flex-col sm:flex-row gap-1 sm:gap-3"><span className="ee-overline sm:w-28 flex-shrink-0">Lead time</span><span className="text-[#A8A29E]">{product.lead_time}</span></div>}
              {product.care && <div className="flex flex-col sm:flex-row gap-1 sm:gap-3"><span className="ee-overline sm:w-28 flex-shrink-0">Care</span><span className="text-[#A8A29E]">{product.care}</span></div>}
              <div className="flex flex-col sm:flex-row gap-1 sm:gap-3" data-testid="product-returns"><span className="ee-overline sm:w-28 flex-shrink-0">Returns</span><span className="text-[#A8A29E]">{product.category === "jewellery" ? "Bespoke, made to order — non-returnable & non-refundable." : "Returnable within 14 days if unopened, unused & in original condition."} <Link to="/returns" className="ee-gold-text hover:underline">Full policy</Link></span></div>
            </div>
          )}

          <div className="mt-10 space-y-3">
            <button
              onClick={onBuyNow}
              data-testid="buy-now-btn"
              className="w-full bg-[#D4AF37] text-[#0C0A09] py-5 uppercase tracking-[0.15em] text-sm hover:bg-[#B5952F] transition-colors flex items-center justify-center gap-2"
            >
              <Zap size={17} /> Buy Now · {eur(price)}
            </button>
            <button
              onClick={onAdd}
              data-testid="add-to-cart-btn"
              className="w-full border border-[#D4AF37] text-[#D4AF37] py-5 uppercase tracking-[0.15em] text-sm hover:bg-[#D4AF37] hover:text-[#0C0A09] transition-colors flex items-center justify-center gap-2"
            >
              {added ? (<><Check size={18} /> Added to cart</>) : "Add to Cart"}
            </button>
          </div>

          {/* On-page express wallets (Apple Pay / Google Pay / Link) — quick single-item buy */}
          <ExpressCheckout product={product} variant={variant} price={price} />

          {product.category === "jewellery" && (
            <div className="mt-10 border ee-hairline bg-[#1C1917] p-7" data-testid="bespoke-process">
              <p className="ee-overline mb-1 ee-gold-text">Bespoke · Made to your measurements</p>
              <p className="text-[#A8A29E] text-sm leading-relaxed mb-6">
                Every bangle is handcrafted to order. Once you order, our team personally guides the rest.
              </p>
              <ol className="space-y-4">
                {[
                  ["Place your order", "Secure your bespoke piece online."],
                  ["We contact you", "Our craftspeople reach out to capture your exact wrist measurements and any unique details you'd like in the design."],
                  ["Handcrafted to order", "Made by hand and delivered to you, with tracking."],
                ].map(([t, d], i) => (
                  <li key={t} className="flex gap-4">
                    <span className="flex-shrink-0 h-6 w-6 rounded-full border border-[#D4AF37] text-[#D4AF37] flex items-center justify-center text-xs font-display">{i + 1}</span>
                    <div>
                      <p className="text-[#FAFAF9] text-sm leading-tight">{t}</p>
                      <p className="text-[#A8A29E] text-xs mt-1 leading-relaxed">{d}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          )}

          {(product.horsehair || product.maintenance) && (
            <div className="mt-10 border-t ee-hairline pt-8 space-y-6">
              {product.horsehair && (
                <div data-testid="product-horsehair">
                  <p className="ee-overline mb-2 ee-gold-text">Required horsehair</p>
                  <p className="text-[#A8A29E] text-sm leading-relaxed">{product.horsehair}</p>
                </div>
              )}
              {product.maintenance && (
                <div data-testid="product-maintenance">
                  <p className="ee-overline mb-2 ee-gold-text">Maintenance set</p>
                  <p className="text-[#A8A29E] text-sm leading-relaxed">{product.maintenance}</p>
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-3 gap-4 mt-10 pt-8 border-t ee-hairline text-center">
            {[[ShieldCheck, "Secure checkout"], [Truck, "Worldwide shipping"], [Sparkles, "Made to your measure"]].map(([Icon, label]) => (
              <div key={label} className="flex flex-col items-center gap-2">
                <Icon strokeWidth={1} size={22} className="ee-gold-text" />
                <span className="text-xs text-[#A8A29E]">{label}</span>
              </div>
            ))}
          </div>

          {product.faqs?.length > 0 && (
            <div className="mt-10 border-t ee-hairline pt-8" data-testid="product-faq">
              <p className="ee-overline mb-3">Questions &amp; answers</p>
              <Accordion type="single" collapsible>
                {product.faqs.map((f, i) => (
                  <AccordionItem key={i} value={`faq-${i}`} className="ee-hairline" data-testid={`product-faq-${i}`}>
                    <AccordionTrigger className="font-display text-base text-[#FAFAF9] hover:no-underline hover:text-[#D4AF37] py-4 text-left">
                      {f.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-[#A8A29E] leading-relaxed pb-5 text-sm">
                      {f.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          )}
        </div>
      </div>

      {/* Buy Now merge prompt */}
      <AnimatePresence>
        {showBuyModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[200] flex items-center justify-center px-6 bg-black/70 backdrop-blur-sm"
            onClick={() => setShowBuyModal(false)}
            data-testid="buy-now-modal"
          >
            <motion.div
              initial={{ opacity: 0, y: 24, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 24, scale: 0.98 }}
              transition={{ duration: 0.3 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-md border ee-hairline bg-[#161210] p-9"
            >
              <button onClick={() => setShowBuyModal(false)} data-testid="buy-now-modal-close" className="absolute top-5 right-5 text-[#A8A29E] hover:text-[#D4AF37]">
                <X size={18} />
              </button>
              <p className="ee-overline mb-3">You already have items in your cart</p>
              <h3 className="font-display text-3xl tracking-tight">How would you like to checkout?</h3>
              <p className="text-[#A8A29E] text-sm mt-4 leading-relaxed">
                Checkout just <span className="text-[#FAFAF9]">{product.name}</span>, or add it to your cart and pay for everything together.
              </p>
              <div className="mt-8 space-y-3">
                <button onClick={mergeAndCheckout} data-testid="modal-merge-btn" className="w-full bg-[#D4AF37] text-[#0C0A09] py-4 uppercase tracking-[0.15em] text-sm hover:bg-[#B5952F] transition-colors">
                  Add to cart &amp; checkout all
                </button>
                <button onClick={buyOnlyThis} data-testid="modal-single-btn" className="w-full border border-[#D4AF37] text-[#D4AF37] py-4 uppercase tracking-[0.15em] text-sm hover:bg-[#D4AF37] hover:text-[#0C0A09] transition-colors">
                  Checkout this item only
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
